import { Box, Typography } from "@mui/material";
import { ResolvedColors } from "../colorResolver";

interface IHomePreviewProps {
  colors: ResolvedColors;
  images: { home_background?: string; home_logo?: string; placeholder?: string };
}

export const HomePreview = ({ colors, images }: IHomePreviewProps) => {
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        backgroundColor: colors.homeListBackgroundColor,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          height: 220,
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: colors.primaryColorDarker,
          backgroundImage: images.home_background ? `url(${images.home_background})` : undefined,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* Menu button - on Home it gets a circular backdrop behind it */}
        <Box
          sx={{
            position: "absolute",
            top: 10,
            left: 10,
            width: 32,
            height: 32,
            borderRadius: "50%",
            backgroundColor: colors.headerButtonBackgroundColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box sx={{ width: 16, height: 12, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
            {[0, 1, 2].map((i) => (
              <Box key={i} sx={{ height: 2, borderRadius: 1, backgroundColor: colors.headerButtonColor }} />
            ))}
          </Box>
        </Box>
        {images.home_logo ? (
          <Box
            component="img"
            src={images.home_logo}
            sx={{ maxWidth: "60%", maxHeight: "60%", objectFit: "contain" }}
          />
        ) : (
          <Typography sx={{ color: colors.primaryColorText, opacity: 0.6 }} variant="caption">
            LOGO
          </Typography>
        )}
      </Box>

      {/* Countdown - top/bottom border colors frame this section directly against whatever
          follows, so they stay visible even when a border shares its default with a neighbor */}
      <Box
        sx={{
          borderTop: `4px solid ${colors.countdownTopBorderColor}`,
          borderBottom: `4px solid ${colors.countdownBottomBorderColor}`,
          backgroundColor: colors.countdownBackgroundColor,
          py: 2,
          textAlign: "center",
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: colors.countdownTextColor,
            letterSpacing: 1,
            fontWeight: 700,
            display: "block",
            mb: 1.5,
          }}
        >
          30 MAY 2026
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "space-around" }}>
          {[
            { value: "46", label: "Days" },
            { value: "16", label: "Hours" },
            { value: "44", label: "Min" },
            { value: "28", label: "Sec" },
          ].map((item) => (
            <Box key={item.label}>
              <Typography
                sx={{ color: colors.countdownTextColor, fontWeight: 800, fontSize: 22, lineHeight: 1 }}
              >
                {item.value}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.countdownTextColor, opacity: 0.75 }}>
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* News list - full width, no side margin, matching the real FlatList items */}
      {[0, 1].map((i) => (
        <Box
          key={i}
          sx={{
            mt: i === 0 ? 0 : 1.5,
            borderTop: `3px solid ${colors.newsItemContentTopBorderColor}`,
            borderBottom: `3px solid ${colors.newsItemContentBottomBorderColor}`,
            backgroundColor: colors.cardColor,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              height: 70,
              backgroundColor: colors.newsImageBackgroundColor,
              backgroundImage: images.placeholder ? `url(${images.placeholder})` : undefined,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
          <Box sx={{ p: 1 }}>
            <Typography variant="caption" sx={{ color: colors.alternativeTextColor }}>
              News headline goes here
            </Typography>
          </Box>
        </Box>
      ))}

      <ArtistSwiperPreview colors={colors} images={images} />
    </Box>
  );
};

const SWIPER_CARDS = [
  { liked: true, priorityKey: "priorityOneColor" as const },
  { liked: false, priorityKey: undefined },
  { liked: false, priorityKey: undefined },
];

const ArtistSwiperPreview = ({ colors, images }: IHomePreviewProps) => (
  <Box sx={{ mt: 2, backgroundColor: colors.countdownBackgroundColor, py: 1.5 }}>
    <Typography
      variant="caption"
      sx={{ color: colors.countdownTextColor, display: "block", px: 2, mb: 1, letterSpacing: 1 }}
    >
      UP NEXT (shown once the countdown ends)
    </Typography>
    <Box sx={{ display: "flex", gap: 1.5, px: 2, overflowX: "auto" }}>
      {SWIPER_CARDS.map((card, i) => (
        <Box
          key={i}
          sx={{
            width: 150,
            flexShrink: 0,
            borderRadius: 1,
            border: "1px solid black",
            overflow: "hidden",
            backgroundColor: colors.primaryColor,
          }}
        >
          <Box
            sx={{
              height: 62,
              backgroundColor: colors.primaryColorDarker,
              backgroundImage: images.placeholder ? `url(${images.placeholder})` : undefined,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
          <Box sx={{ px: 1, pt: 0.75, pb: 0.5 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography sx={{ color: colors.primaryColorText, fontSize: 8 }}>
                Fri 18:00
              </Typography>
              <Typography sx={{ color: colors.primaryColorText, fontSize: 8 }}>
                Main
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.25 }}>
              <Typography
                noWrap
                sx={{
                  color: colors.primaryColorText,
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: "uppercase",
                }}
              >
                Wolf Parade
              </Typography>
              <Typography
                sx={{
                  color: card.liked && card.priorityKey ? colors[card.priorityKey] : colors.primaryColorText,
                  fontSize: 14,
                  lineHeight: 1,
                }}
              >
                {card.liked ? "★" : "☆"}
              </Typography>
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  </Box>
);
